import { ImageDataLike } from './ocr-types'

export interface TextLineRegion {
  x: number
  y: number
  width: number
  height: number
  lineNumber: number
}

export class OcrDetector {
  detectLines(pageImage: ImageDataLike): TextLineRegion[] {
    const { data: pixels, width: imgW, height: imgH } = pageImage

    const gray = new Uint8Array(imgW * imgH)
    for (let i = 0; i < gray.length; i++) {
      gray[i] = pixels[i * 4] ?? 0
    }

    const projection = new Float64Array(imgH)
    for (let y = 0; y < imgH; y++) {
      let sum = 0
      const rowOffset = y * imgW
      for (let x = 0; x < imgW; x++) {
        sum += 255 - (gray[rowOffset + x] ?? 0)
      }
      projection[y] = sum / imgW
    }

    const sorted = Float64Array.from(projection).sort()
    const medianIdx = Math.floor(sorted.length / 2)
    const bgLevel = sorted[Math.floor(sorted.length * 0.2)] ?? 0
    const median = sorted[medianIdx] ?? 0
    const threshold = bgLevel + (median - bgLevel) * 0.3

    const minLineHeight = Math.max(4, Math.floor(imgH * 0.003))
    const regions: { start: number; end: number }[] = []
    let inLine = false
    let lineStart = 0

    for (let y = 0; y < imgH; y++) {
      const val = projection[y] ?? 0
      if (val > threshold) {
        if (!inLine) {
          lineStart = y
          inLine = true
        }
      } else {
        if (inLine) {
          const lineHeight = y - lineStart
          if (lineHeight >= minLineHeight) {
            regions.push({ start: lineStart, end: y })
          }
          inLine = false
        }
      }
    }
    if (inLine && imgH - lineStart >= minLineHeight) {
      regions.push({ start: lineStart, end: imgH })
    }

    const lines: TextLineRegion[] = []
    let lineNumber = 0

    for (const region of regions) {
      let left = imgW
      let right = 0

      for (let y = region.start; y < region.end; y++) {
        const rowOffset = y * imgW
        for (let x = 0; x < imgW; x++) {
          if ((gray[rowOffset + x] ?? 255) < 180) {
            if (x < left) left = x
            if (x > right) right = x
          }
        }
      }

      if (right <= left) continue

      const padX = Math.floor((right - left) * 0.02)
      const padY = Math.floor((region.end - region.start) * 0.1)

      lineNumber++
      lines.push({
        x: Math.max(0, left - padX),
        y: Math.max(0, region.start - padY),
        width: Math.min(imgW, right + padX) - Math.max(0, left - padX),
        height: Math.min(imgH, region.end + padY) - Math.max(0, region.start - padY),
        lineNumber,
      })
    }

    return lines
  }
}
